import type { Status, StatusOverride } from '../types';

/**
 * INV-11, LIVE-3, LIVE-12, DISC-4, SC-6 — render order, derived and never stored.
 *
 * Two orders, one module (design §1.2), and they take **different inputs on purpose**:
 *
 * | | locked board | Discovery |
 * |---|---|---|
 * | function | `lockedOrder` | `discoveryOrder` |
 * | sorts by | `(isActive, boardPosition)` | `(count desc, first observation, createdAt, id)` |
 * | count-driven reflow | **forbidden** (LIVE-12) | required (DISC-4) |
 *
 * ── Why `lockedOrder` takes the override and not the status ──────────────────
 * INV-11's phrasing — "a pure function of `(status, boardPosition)`" — permits an unsafe
 * implementation. `status` is *derived from the count* (design §3.3), so ranking the four-valued
 * enum would launder the count into the order and the third tap on a hypothesis would move its
 * tile. That is precisely what LIVE-12 and SC-6 forbid. INV-11's behavioral text — "active tiles in
 * `boardPosition` order first, disproved and invalidated tiles below" — is unambiguous, and it is
 * what is implemented: the comparator sees the **dismissal dimension only**.
 *
 * ── How that is enforced rather than promised ────────────────────────────────
 * `BoardTile` does not declare `cachedCount` or `status`. `lockedOrder` is generic over
 * `T extends BoardTile`, so a caller may pass whole hypothesis rows — but the function body can
 * only see the three fields the constraint declares. Reading a count inside it does not compile.
 * (Design §6.3 phrases this as "`cachedCount` is not imported in `order.ts`"; the module also holds
 * `discoveryOrder`, which needs the count by requirement, so the guarantee is per-function and
 * carried by the parameter type — which is the stronger form anyway.)
 *
 * Both comparators are **total**: every tie falls through to `id`, compared by code point rather
 * than `localeCompare`, so the result cannot vary with the host's locale. LOCK-2 and DISC-4 both
 * promise determinism and a partial order cannot deliver it.
 */

/** The locked board's tile. Deliberately carries no count and no derived status. */
export interface BoardTile {
  readonly id: string;
  readonly statusOverride: StatusOverride;
  readonly boardPosition: number | null;
}

/** A Discovery row. DISC-4 sorts these by count, so the count is declared here and only here. */
export interface DiscoveryRow {
  readonly id: string;
  readonly cachedCount: number;
  /**
   * `createdAt` of this hypothesis's earliest `increment`, or `null` when it has none.
   *
   * DISC-4 and LOCK-2 both tie-break on "earliest first observation". A hypothesis with zero
   * increments has none — which is why the chain continues past this term (§19 F-11).
   */
  readonly firstObservationCreatedAt: number | null;
  /** When the hypothesis row itself was created. Third term, so the order is total. */
  readonly createdAt: number;
}

type Positioned<T> = T & { boardPosition: number };

function isPositioned<T extends BoardTile>(tile: T): tile is Positioned<T> {
  return tile.boardPosition !== null;
}

/** INV-11 / design §6.3 — the dismissal dimension, and nothing else, decides "active". */
export function isActive(tile: { readonly statusOverride: StatusOverride }): boolean {
  return tile.statusOverride === null;
}

/** Code-point comparison. `localeCompare` is locale-dependent and would break LOCK-2's determinism. */
function compareIds(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

function byBoardPosition<T extends BoardTile>(a: Positioned<T>, b: Positioned<T>): number {
  return a.boardPosition - b.boardPosition || compareIds(a.id, b.id);
}

/**
 * INV-11 — the locked board's rendered tile order.
 *
 * Active tiles in `boardPosition` order first, then dismissed tiles in `boardPosition` order
 * (LIVE-3: "disproved and invalidated tiles sink below all active tiles"). List-only hypotheses
 * (`boardPosition === null`) are not tiles and are excluded — they appear on Discovery pages and in
 * lists, never in a grid slot.
 *
 * **The render is packed, and this is a partition-and-sort, never a position-indexed grid** (design
 * §6.2). Because `board.ts` allocates positions as a contiguous prefix, the packed result *happens
 * to* place each active tile in the grid cell matching its position — but indexing by position
 * would put a sunk tile's hole inside the active region and break LIVE-3.
 *
 * `boardPosition` is never mutated by a status change (INV-4), so un-disproving a tile returns it
 * to its exact original slot. That is a consequence of this function reading a position it cannot
 * write, not a separate mechanism.
 */
export function lockedOrder<T extends BoardTile>(tiles: readonly T[]): T[] {
  const positioned = tiles.filter(isPositioned);
  const active: Positioned<T>[] = [];
  const sunk: Positioned<T>[] = [];
  for (const tile of positioned) {
    (isActive(tile) ? active : sunk).push(tile);
  }
  active.sort(byBoardPosition);
  sunk.sort(byBoardPosition);
  return [...active, ...sunk];
}

/**
 * DISC-4 / LOCK-2 — `(cachedCount desc, firstObservationCreatedAt asc, createdAt asc, id asc)`.
 *
 * Exported because `lock.ts` must rank by the identical order: LOCK-2's "given identical
 * observation logs the suggestion is identical every time" and DISC-4's stable sort are the same
 * promise, and two comparators would be two chances to break it.
 *
 * A `null` first-observation sorts last. Given INV-2 that branch is unreachable within a count tie
 * — `cachedCount > 0` implies a first increment exists, and two zero-count rows both have `null` —
 * so it decides nothing; it is here to keep the order total against corrupt input.
 */
export function compareDiscovery(a: DiscoveryRow, b: DiscoveryRow): number {
  if (a.cachedCount !== b.cachedCount) return b.cachedCount - a.cachedCount;

  const first = compareNullableAsc(a.firstObservationCreatedAt, b.firstObservationCreatedAt);
  if (first !== 0) return first;

  if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
  return compareIds(a.id, b.id);
}

function compareNullableAsc(a: number | null, b: number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}

/**
 * A row the two **read-aloud** rankings order — HT-5's adjustment list (§7.3) and §10.2's
 * distillation list.
 *
 * `status` is the **materialized** four-valued enum (§3.3), and reading it here is correct in a way
 * it is deliberately *not* in `lockedOrder`: INV-11/LIVE-12 forbid count-driven reflow of *tiles*,
 * which is why `BoardTile` cannot see a count or a status at all. Neither of these lists is the
 * board — both are recomputed on entry to a screen the user reads from, and both requirements ask
 * for confirmed-first in those words.
 *
 * Deliberately **no dismissal dimension and no artifact phase.** §7.3 excludes dismissed rows and
 * §10.2 excludes nothing, so eligibility is the caller's; and DIST-2's *"no privilege and no
 * exclusion"* for halftime-promoted hypotheses is enforced by this type — a comparator whose input
 * cannot see whether a row was promoted at halftime cannot rank on it (§6.3's argument, reused).
 */
export interface RankedByStatus extends DiscoveryRow {
  readonly status: Status;
}

/**
 * §7.3 and §10.2 — *"`confirmed` first, then `cachedCount` desc, then the total tiebreak chain of
 * §6.1."*
 *
 * **One comparator, two requirements**, because they are the same sentence. HT-5 and distillation
 * differ in what they *filter* — §7.3 drops dismissed rows, §10.2 drops nothing — and not in how
 * they *order*, so the filter is the caller's and the order is here. Restating the two terms at the
 * second call site is `new-path-parity-single-source-the-effect` exactly: a second producer of an
 * effect that already exists, free to drift from the first, with nothing that would report it.
 *
 * Total, because `compareDiscovery` is (§6.1's chain ends at `id`).
 */
export function compareConfirmedFirst(a: RankedByStatus, b: RankedByStatus): number {
  const aConfirmed = a.status === 'confirmed';
  const bConfirmed = b.status === 'confirmed';
  if (aConfirmed !== bConfirmed) return aConfirmed ? -1 : 1;
  return compareDiscovery(a, b);
}

/**
 * DISC-4 — rows within a Discovery page, descending by count, stable, ties by earliest first
 * observation.
 *
 * Count-driven reordering is permitted **only** in Discovery (DISC-4, LIVE-12). This function
 * computes the *target* order; DISC-7's guarantee that no row moves within `REORDER_QUIET_MS` of a
 * tap is a property of when the caller applies it, which is why the quiet-window gate lives in view
 * state and this module stays pure (design §5.5).
 *
 * Returns a new array — the input is never sorted in place, so a caller cannot accidentally mutate
 * the list a render is already walking.
 */
export function discoveryOrder<T extends DiscoveryRow>(rows: readonly T[]): T[] {
  return [...rows].sort(compareDiscovery);
}
